import { useState, useRef, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import {
  // ButtonSecondary,
  // IconDownload,
  // IconUpload,
  // ButtonSize,
  Table,
  Dropzone,
  IconDropzone,
  useQueryState,
  useGetList,
  useScroll,
  useFilters,
  usePrevious,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { onFileUpload } from '../store/slices/sic-codes';
import {
  sicCodesListColumns,
  sicCodesListColumnsLabels,
  sicCodesListFiltersSchema,
  sicCodesListSchema,
} from '../variables/sic-codes';
import { useLazyGetSicCodesQuery } from '../store/services';
import {
  useLazyGetAmlDictionariesQuery,
  useLazyGetAmlBaseCurrenciesQuery,
  useLazyGetAmlCurrenciesQuery,
} from '../../../store/dictionaries/services';
import { SicCode } from '../../types';
import { RiskScoreRange } from '../../../store/dictionaries/types';

import cn from '../corporate-clients-risk.module.css';

const formatSicCodesList = (
  list: SicCode[],
  riskScoreRanges?: RiskScoreRange[],
): SicCode[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const riskCategory = riskScoreRanges?.find(
      (riskScore) =>
        riskScore?.from <= item.riskScore && riskScore.to >= item.riskScore,
    );

    const render: Record<string, React.JSX.Element> = {
      riskCategory: (
        <div>{`${riskCategory?.name?.charAt(0)}${riskCategory?.name
          .slice(1)
          .toLowerCase()} risk`}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const SicCodes = () => {
  const [dropzoneVisible, setDropzoneVisible] = useState(true);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    corporateClientsRisk: {
      sicCodes: {
        list,
        uploadSuccess,
        uploadInProgress,
        pagination,
        errorsList,
      },
    },
    amlDictionaries: {
      riskScoreRanges,
      activePermissions,
      currencies,
      baseCurrency,
    },
  } = useAppSelector((state) => state);

  const [getDictionaries] = useLazyGetAmlDictionariesQuery();
  const [getAmlBaseCurrency] = useLazyGetAmlBaseCurrenciesQuery();
  const [getAmlCurrencies] = useLazyGetAmlCurrenciesQuery();

  const dispatch = useAppDispatch();

  const { onSortChange, filters, onFilterChange } =
    useQueryState(sicCodesListSchema);

  const prevFilters = usePrevious(filters);

  const [getSicCodes, { isLoading, isFetching }] = useLazyGetSicCodesQuery();

  const formattedData = useMemo(() => {
    const data = formatSicCodesList(list as SicCode[], riskScoreRanges);

    return data;
  }, [list, riskScoreRanges]);

  const { getListAction, setPage, onFilter, errorList } = useGetList<SicCode>({
    getList: getSicCodes,
    onFilterChange,
    onSortChange,
    schema: sicCodesListSchema,
  });

  const onUpload = (data: FileList) => {
    const file = data[0];

    dispatch(onFileUpload(file));

    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  };

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: sicCodesListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (uploadSuccess) {
      setDropzoneVisible(false);
    }
  }, [uploadSuccess]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !list &&
      activePermissions?.includes(PermissionEnum.SETTINGS_SIC_CODES_LIST)
    ) {
      getListAction({
        filters,
      });
    }
  }, [filters, prevFilters, list, activePermissions]);

  useEffect(() => {
    if (list && list.length) {
      setDropzoneVisible(false);
    }
  }, [list]);

  useEffect(() => {
    if (activePermissions?.includes(PermissionEnum.SETTINGS_SIC_CODES_LIST)) {
      if (!riskScoreRanges) {
        getDictionaries();
      }

      if (!currencies) {
        getAmlCurrencies();
      }

      if (!baseCurrency) {
        getAmlBaseCurrency();
      }
    }
  }, [activePermissions, riskScoreRanges, currencies, baseCurrency]);

  useEffect(() => {
    let title = 'SIC codes';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;
  let globalContent = (
    <div className={cn.sicCodesLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (activePermissions.includes(PermissionEnum.SETTINGS_SIC_CODES_LIST)) {
      content = (
        <div className={cn.sicCodesLoader}>
          <IconLoader />
        </div>
      );

      if (!isLoading && dropzoneVisible) {
        content = (
          <div className={cn.dropzoneWrapper}>
            <Dropzone
              inputRef={inputRef}
              onUpload={onUpload}
              // accept=".csv, text/xml"
              dropzoneIcon={<IconDropzone />}
              fileUploadInProgress={uploadInProgress}
              dropzoneText="Drag your CSV or XML file here"
              dropzoneClassName={cn.dropzone}
            />
          </div>
        );
      } else if (!isLoading && !dropzoneVisible) {
        content = (
          <Table
            columns={sicCodesListColumns}
            data={formattedData || []}
            onSort={() => {}}
            filters={filters}
            sort={{}}
            columnsLabels={sicCodesListColumnsLabels}
            onFilterChange={(values) => onFilter(values)}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            take="30"
            error={errorsList || errorList}
            setPage={(data) => setPage({ ...data, filters })}
            pagination={pagination}
            tableFormClassName={cn.sicCodesTableForm}
            // topContent={
            //   <div className={cn.sicCodesTop}>
            //     <div className={cn.sicCodesControls}>
            //       <ButtonSecondary
            //         size={ButtonSize.SMALL}
            //         className={`button-white ${cn.sicCodesControlButton}`}
            //         text={dropzoneVisible ? 'Show list' : 'Upload list file'}
            //         icon={<IconUpload />}
            //         onClick={() => setDropzoneVisible(!dropzoneVisible)}
            //       />
            //       <ButtonSecondary
            //         size={ButtonSize.SMALL}
            //         className={`button-white ${cn.sicCodesControlButton}`}
            //         text="Download list"
            //         icon={<IconDownload />}
            //       />
            //     </div>
            //   </div>
            // }
            clickable
          />
        );
      }

      globalContent = (
        <div className={cn.sicCodes}>
          <div className={cn.sicCodesTableWrapper} ref={scrollRef}>
            {content}
          </div>
          <Outlet />
        </div>
      );
    } else {
      globalContent = <PermissionDenied />;
    }
  }

  return globalContent;
};

export default SicCodes;
